'use client';

import parse, {
  Element,
  HTMLReactParserOptions,
  Text,
} from 'html-react-parser';
import { useMemo, useEffect, useId, useRef, useState } from 'react';

interface ScriptData {
  src?: string;
  inline?: string;
  attributes?: Record<string, string>;
}

export interface UseHtmlWithScriptsOptions {
  /**
   * Enable lazy loading - scripts only load when container is visible.
   * Default: true
   */
  lazy?: boolean;
  /**
   * Root margin for Intersection Observer (how early to start loading).
   * Default: '200px'
   */
  lazyRootMargin?: string;
  /**
   * Ref to the container element for lazy loading detection.
   * Required when lazy loading is enabled.
   */
  containerRef?: React.RefObject<HTMLElement | null>;
}

export interface UseHtmlWithScriptsResult {
  content: React.ReactNode;
  scriptSources: string[];
  /** Whether lazy loading is active and waiting for visibility */
  isWaitingForVisibility: boolean;
}

/**
 * Creates a <script> element from extracted script data, applies its preserved
 * attributes, appends it to <body>, and records it in `added` for later cleanup.
 * External scripts are cache-busted and any prior copy sharing the same base URL
 * is removed first. Kept at module level so the effect below stays shallow.
 */
function appendScript(scriptData: ScriptData, added: HTMLScriptElement[]): void {
  const script = document.createElement('script');

  // Apply all preserved attributes (id, type, data-*, etc.)
  if (scriptData.attributes) {
    for (const [key, value] of Object.entries(scriptData.attributes)) {
      script.setAttribute(key, value);
    }
  }

  if (scriptData.src) {
    // External script - remove existing and reload with cache-busting
    const baseUrl = scriptData.src.split('?')[0];
    // Filter in JS to avoid CSS selector issues with special characters
    const existingScripts = Array.from(
      document.querySelectorAll('script')
    ).filter((s) => s.src.split('?')[0] === baseUrl);
    existingScripts.forEach((s) => { s.remove(); });

    const cacheBustSrc = `${scriptData.src}${scriptData.src.includes('?') ? '&' : '?'}_t=${Date.now()}`;
    script.src = cacheBustSrc;
    // Only set async if not explicitly specified in attributes
    if (!scriptData.attributes?.async) {
      script.async = true;
    }
    document.body.appendChild(script);
    added.push(script);
  } else if (scriptData.inline) {
    // Inline script - execute by creating a new script element
    script.textContent = scriptData.inline;
    document.body.appendChild(script);
    added.push(script);
  }
}

/**
 * Hook that parses HTML content, extracts script tags, and reloads them on every mount.
 *
 * This ensures third-party scripts (like HubSpot, Curator, etc.) work correctly with
 * client-side navigation, as they typically only scan the DOM once on load.
 *
 * Supports lazy loading - scripts only load when the container becomes visible
 * in the viewport (enabled by default).
 *
 * @param html - Raw HTML string that may contain script tags
 * @param options - Configuration options for lazy loading
 * @returns Object with parsed content, script sources, and loading state
 */
export function useHtmlWithScripts(
  html: string | undefined | null,
  options: UseHtmlWithScriptsOptions = {}
): UseHtmlWithScriptsResult {
  const { lazy = true, lazyRootMargin = '200px', containerRef } = options;
  const instanceId = useId();
  const [isVisible, setIsVisible] = useState(!lazy);

  const { content, scripts } = useMemo(() => {
    if (!html) return { content: null, scripts: [] };

    // Normalize line endings (Windows \r\n to Unix \n)
    const normalizedHtml = html.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const extractedScripts: ScriptData[] = [];

    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        // Extract and filter out script tags
        if (domNode instanceof Element && domNode.name === 'script') {
          const src = domNode.attribs.src;
          // Extract all attributes except src (handled separately).
          // domhandler always initialises `attribs` ({} at minimum) — no guard.
          const attributes: Record<string, string> = {};
          for (const [key, value] of Object.entries(domNode.attribs)) {
            if (key !== 'src') {
              attributes[key] = value;
            }
          }

          if (src) {
            // External script with all attributes
            extractedScripts.push({ src, attributes });
          } else {
            // Inline script - get the text content
            const textChild = domNode.children.find(
              // `type` is domhandler's ElementType, a string enum where the
              // 'text' node is literally 'text'. The comparison is correct and
              // runtime-safe; instanceof is unreliable across monorepo module
              // copies, and the enum value lives in domelementtype (a nested dep
              // we deliberately don't promote to a direct one for one check).
              // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
              (child): child is Text => child.type === 'text'
            );
            if (textChild?.data) {
              extractedScripts.push({ inline: textChild.data, attributes });
            }
          }
          // Return empty fragment to remove script from rendered output
          return <></>;
        }
      },
    };

    return {
      content: parse(normalizedHtml, options),
      scripts: extractedScripts,
    };
  }, [html]);

  // Track mounted state to handle React Strict Mode double-invocation
  const isMountedRef = useRef(false);
  const addedScriptsRef = useRef<HTMLScriptElement[]>([]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isVisible) return undefined;

    const element = containerRef?.current;
    if (!element) {
      // No container ref provided, fall back to immediate loading
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: lazyRootMargin }
    );

    observer.observe(element);
    return () => { observer.disconnect(); };
  }, [lazy, isVisible, lazyRootMargin, containerRef]);

  // Execute scripts on every mount for client-side navigation
  // Only runs when visible (for lazy loading)
  useEffect(() => {
    isMountedRef.current = true;

    if (scripts.length === 0) return undefined;
    if (lazy && !isVisible) return undefined; // Wait for visibility

    // Small delay to ensure DOM container is ready
    const timeoutId = setTimeout(() => {
      // Check if still mounted (handles React Strict Mode cleanup/re-run)
      if (!isMountedRef.current) {
        return;
      }

      scripts.forEach((scriptData) => {
        appendScript(scriptData, addedScriptsRef.current);
      });
    }, 100);

    return () => {
      // Mark as unmounted and clear timeout
      isMountedRef.current = false;
      clearTimeout(timeoutId);
      // Remove scripts on unmount
      if (addedScriptsRef.current.length > 0) {
        addedScriptsRef.current.forEach((script) => { script.remove(); });
        addedScriptsRef.current = [];
      }
    };
  }, [scripts, instanceId, lazy, isVisible]);

  // Return scriptSources for backwards compatibility
  const scriptSources = scripts.flatMap((s) => (s.src ? [s.src] : []));
  return {
    content,
    scriptSources,
    isWaitingForVisibility: lazy && !isVisible,
  };
}
